import React, { useState, useRef } from "react";
import { FaImage, FaUpload, FaTimes, FaCheckCircle, FaExclamationCircle, FaGlobe, FaGraduationCap, FaUniversity } from "react-icons/fa";
import { useSnackBar } from "../../hooks/useSnackBar";

const UniversityLogoUpload = () => {
  const [logoType, setLogoType] = useState("university");
  const [logoName, setLogoName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const enqMsg = useSnackBar();

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 200KB for images)
      const maxSize = 200 * 1024; // 200KB
      if (file.size > maxSize) {
        enqMsg('File size too large. Please upload an image less than 200KB.', "error");
        return;
      }
      
      setLogoFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getLogoTypeIcon = (type) => {
    switch (type) {
      case "university":
        return <FaUniversity className="w-5 h-5" />;
      case "country":
        return <FaGlobe className="w-5 h-5" />;
      case "discipline":
        return <FaGraduationCap className="w-5 h-5" />;
      default:
        return <FaImage className="w-5 h-5" />;
    }
  };

  const getLogoTypeLabel = (type) => {
    switch (type) {
      case "university":
        return "University";
      case "country":
        return "Country";
      case "discipline":
        return "Discipline";
      default:
        return "Logo";
    }
  };

  const getPlaceholderText = (type) => {
    switch (type) {
      case "university":
        return "Enter university name (e.g., Stanford University)";
      case "country":
        return "Enter country name (e.g., United States)";
      case "discipline":
        return "Enter discipline name (e.g., Computer Science)";
      default:
        return "Enter name";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!logoName.trim()) {
      enqMsg(`${getLogoTypeLabel(logoType)} name is required.`, "error");
      return;
    }

    if (!logoFile) {
      enqMsg('Please select a logo file to upload.', "error");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('type', logoType);
      formData.append('name', logoName.trim());
      formData.append('logo', logoFile);

      const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:8181' : ''}/api/admin/opportunities/upload-logo`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      const result = await response.json();

      if (response.ok) {
        enqMsg(result.message || `${getLogoTypeLabel(logoType)} logo uploaded successfully!`, "success");
        // Reset form
        setLogoName("");
        removeLogo();
      } else {
        throw new Error(result.error || 'Failed to upload logo');
      }
    } catch (error) {
      enqMsg(error.message || 'Failed to upload logo', "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#DB4F40] mb-2">Logo Upload</h1>
          <p className="text-gray-600">Upload logos for universities, countries, and disciplines to be used across the platform</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Logo Type Selection */}
          <div>
            <label className="block text-base font-semibold text-[#1B222A] mb-3">
              Logo Type *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { value: "university", label: "University", icon: FaUniversity, color: "bg-blue-50 border-blue-200 text-blue-700" },
                { value: "country", label: "Country", icon: FaGlobe, color: "bg-green-50 border-green-200 text-green-700" },
                { value: "discipline", label: "Discipline", icon: FaGraduationCap, color: "bg-purple-50 border-purple-200 text-purple-700" }
              ].map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setLogoType(type.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    logoType === type.value
                      ? `${type.color} border-current`
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <type.icon className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Name Input */}
          <div>
            <label className="block text-base font-semibold text-[#1B222A] mb-3 flex items-center gap-2">
              {getLogoTypeIcon(logoType)}
              {getLogoTypeLabel(logoType)} Name *
            </label>
            <input
              type="text"
              value={logoName}
              onChange={(e) => setLogoName(e.target.value)}
              placeholder={getPlaceholderText(logoType)}
              className="w-full rounded-md border border-[#DB4F40] py-3 px-4 focus:border-[#DB4F40] focus:ring focus:ring-[#DB4F40]/50 transition duration-300 text-lg"
              required
            />
            <p className="text-sm text-gray-500 mt-2">
              The logo will be saved with this exact {getLogoTypeLabel(logoType).toLowerCase()} name
            </p>
          </div>

          {/* Logo Upload Section */}
          <div>
            <label className="block text-base font-semibold text-[#1B222A] mb-3 flex items-center gap-2">
              <FaImage className="text-[#DB4F40] w-5 h-5" />
              {getLogoTypeLabel(logoType)} Logo *
            </label>
            <p className="text-sm text-gray-500 mb-4">Upload a logo for the {getLogoTypeLabel(logoType).toLowerCase()}</p>
            
            <div className="bg-[#FFF6F5] border border-[#FFD6D4] rounded-xl shadow-sm p-6">
              {!logoPreview ? (
                <div className="text-center">
                  <div className="border-2 border-dashed border-[#DB4F40]/30 rounded-lg p-12 hover:border-[#DB4F40]/50 transition-colors">
                    <FaUpload className="mx-auto h-16 w-16 text-[#DB4F40]/50 mb-6" />
                    <p className="text-lg text-gray-600 mb-2">Click to upload or drag and drop</p>
                    <p className="text-sm text-gray-500 mb-6">PNG, JPG, JPEG, WEBP, GIF, SVG (max 200KB)</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-[#DB4F40] text-white px-6 py-3 rounded-lg hover:bg-[#c54538] transition-colors text-base font-medium"
                    >
                      Choose File
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-20 h-20 object-cover rounded-lg border border-[#FFD6D4]"
                    />
                    <div>
                      <p className="text-lg font-medium text-[#1B222A]">{logoFile?.name}</p>
                      <p className="text-sm text-gray-500">
                        {(logoFile?.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="text-[#DB4F40] hover:text-[#c54538] transition-colors p-2"
                  >
                    <FaTimes className="h-6 w-6" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={uploading || !logoName.trim() || !logoFile}
              className="bg-[#DB4F40] text-white px-8 py-3 rounded-lg hover:bg-[#c54538] transition-colors text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <FaUpload className="h-5 w-5" />
                  Upload Logo
                </>
              )}
            </button>
          </div>
        </form>

        {/* Instructions */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <FaCheckCircle className="text-blue-600" />
            Upload Instructions
          </h3>
          <ul className="text-blue-800 space-y-2 text-sm">
            <li>• Logo will be automatically converted to PNG format</li>
            <li>• Maximum file size: 200KB</li>
            <li>• Supported formats: PNG, JPG, JPEG, WEBP, GIF, SVG</li>
            <li>• Logo will be accessible at: <code className="bg-blue-100 px-2 py-1 rounded">/api/images/{logoType}/{logoName || 'Name'}.png</code></li>
            <li>• Uploading a new logo with the same {getLogoTypeLabel(logoType).toLowerCase()} name will replace the existing one</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UniversityLogoUpload;
